import { createBlindOffUpdate, createBlindOnUpdate } from '@module/helpers/centrifuge/messageUpdates';
import { plyrStateSafeLocalStorage } from '@module/lib/storage/storageImplementations';
import type { ParsedPublicMessage } from '@module/types/centrifuge/messageTypes';
import {
  type BlindOffMessage,
  type BlindOnMessage,
  type CastGuestImgBlindOnMessage,
  type CastPauseMessage,
  isBlindOffMessage,
  isBlindOnMessage,
  isCastGuestImgBlindOnMessage,
  isCastPauseMessage,
  isServiceBlindOffMessage,
  isServiceBlindOnMessage,
  type ServiceBlindOffMessage,
  type ServiceBlindOnMessage,
} from '@module/types/centrifuge/public-message/blindMessageTypes';
import type { MessageHandler, MessageHandlerAction, MessageHandlerContext } from '../types';

export class BlindOnHandler implements MessageHandler {
  constructor(
    private customAction?: MessageHandlerAction<
      BlindOnMessage | CastGuestImgBlindOnMessage | CastPauseMessage | ServiceBlindOnMessage
    >,
  ) {}

  canHandle(payload: ParsedPublicMessage): boolean {
    return (
      isBlindOnMessage(payload) ||
      isCastGuestImgBlindOnMessage(payload) ||
      isCastPauseMessage(payload) ||
      isServiceBlindOnMessage(payload)
    );
  }

  handle(
    payload: BlindOnMessage | CastGuestImgBlindOnMessage | CastPauseMessage | ServiceBlindOnMessage,
    context: MessageHandlerContext,
  ): void {
    if (this.customAction === null || this.customAction === undefined) return;
    if (!context.playInfo) return;

    if (typeof this.customAction === 'function') {
      this.customAction(payload, context);
      return;
    }

    // 기본 동작
    // 블라인드 강제 음소거 — 저장된 음소거 기록은 보존.
    context.setMuted?.(true, { persist: false });
    context.setPlayInfo(createBlindOnUpdate(payload, context.playInfo));
  }
}

export class BlindOffHandler implements MessageHandler {
  constructor(private customAction?: MessageHandlerAction<BlindOffMessage | ServiceBlindOffMessage>) {}

  canHandle(payload: ParsedPublicMessage): boolean {
    return isBlindOffMessage(payload) || isServiceBlindOffMessage(payload);
  }

  handle(payload: BlindOffMessage | ServiceBlindOffMessage, context: MessageHandlerContext): void {
    if (this.customAction === null || this.customAction === undefined) return;
    if (!context.playInfo) return;

    if (typeof this.customAction === 'function') {
      this.customAction(payload, context);
      return;
    }

    // 기본 동작
    // CastPause 상태에서는 블라인드 해제하지 않음
    if (context.playInfo.roomBlindInfo?.blindType === 'CastPause') return;

    // 블라인드 해제 — 강제 해제 대신 사용자의 저장된 음소거 상태로 복원.
    context.setMuted?.(plyrStateSafeLocalStorage.get()?.muted ?? true, { persist: false });
    context.setPlayInfo(createBlindOffUpdate(context.playInfo));
  }
}
